/**
 * tableSerializer — THE single table serialization service (CONTRATO v3, O4).
 *
 * Given a native table ProseMirror node (a Node instance OR its JSON), produce
 * multi-format output: `{ gfm, html, hasMerges }`.
 *
 *  - GFM: pipe-syntax markdown for the AI/chat interchange layer.
 *  - HTML: clean `<table><thead><tbody>` (no inline styles) for external paste.
 *
 * The HTML is always the faithful representation. When the table has merged
 * cells (colspan/rowspan > 1) `hasMerges` is true and the GFM is a best-effort
 * flatten (GFM markdown cannot express merges).
 */

/**
 * Normalize a table input to plain JSON. Accepts a ProseMirror Node instance
 * (which exposes `.toJSON()`) or an already-plain JSON object.
 */
const toJSON = (table) => {
  if (!table) return null;
  if (typeof table.toJSON === 'function') return table.toJSON();
  return table;
};

/**
 * Extract the plain text of a single cell node, flattening multiple paragraphs
 * with the given separator.
 *
 * @param {object} cell - tableHeader/tableCell JSON node
 * @param {string} paragraphSep - separator between block children
 * @returns {string}
 */
const cellText = (cell, paragraphSep) => {
  const blocks = Array.isArray(cell?.content) ? cell.content : [];

  const textOfNode = (node) => {
    if (!node || typeof node !== 'object') return '';
    if (typeof node.text === 'string') return node.text;
    if (Array.isArray(node.content)) return node.content.map(textOfNode).join('');
    return '';
  };

  return blocks.map(textOfNode).join(paragraphSep);
};

/** True if the row's cells are all tableHeader nodes. */
const isHeaderRow = (row) => {
  const cells = Array.isArray(row?.content) ? row.content : [];
  return cells.length > 0 && cells.every((c) => c.type === 'tableHeader');
};

/** Escape a value for a GFM table cell (pipes + newlines). */
const escapeGfmCell = (value) =>
  String(value == null ? '' : value)
    .replace(/\|/g, '\\|')
    .replace(/\r?\n/g, '<br>');

/** Escape a value for HTML text content. */
const escapeHtml = (value) =>
  String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

/**
 * Serialize a native table node to `{ gfm, html, hasMerges }`.
 *
 * @param {object} table - native `table` node (Node instance or JSON)
 * @returns {{ gfm: string, html: string, hasMerges: boolean }}
 */
export const serializeTable = (table) => {
  const json = toJSON(table);
  const rows = Array.isArray(json?.content) ? json.content : [];

  if (!rows.length) {
    return { gfm: '', html: '', hasMerges: false };
  }

  // Detect merges (colspan/rowspan > 1 on any cell).
  let hasMerges = false;
  for (const row of rows) {
    for (const cell of row?.content || []) {
      const cs = cell?.attrs?.colspan || 1;
      const rs = cell?.attrs?.rowspan || 1;
      if (cs > 1 || rs > 1) hasMerges = true;
    }
  }

  // --- GFM ---
  // If the first row is a header row, use it; otherwise synthesize an empty header.
  const firstRowIsHeader = isHeaderRow(rows[0]);
  const colCount = rows.reduce(
    (max, r) => Math.max(max, Array.isArray(r?.content) ? r.content.length : 0),
    0
  );

  const gfmRowFrom = (row) => {
    const cells = Array.isArray(row?.content) ? row.content : [];
    const values = Array.from({ length: colCount }, (_, i) =>
      escapeGfmCell(cellText(cells[i], '<br>'))
    );
    return `| ${values.join(' | ')} |`;
  };

  const gfmLines = [];
  let bodyRows;
  if (firstRowIsHeader) {
    gfmLines.push(gfmRowFrom(rows[0]));
    bodyRows = rows.slice(1);
  } else {
    // Synthesize an empty header row so the GFM table is well-formed.
    gfmLines.push(`| ${Array.from({ length: colCount }, () => '').join(' | ')} |`);
    bodyRows = rows;
  }
  gfmLines.push(`| ${Array.from({ length: colCount }, () => '---').join(' | ')} |`);
  for (const row of bodyRows) {
    gfmLines.push(gfmRowFrom(row));
  }
  const gfm = gfmLines.join('\n');

  // --- HTML ---
  const cellHtml = (cell, tag) => {
    const attrs = [];
    const cs = cell?.attrs?.colspan || 1;
    const rs = cell?.attrs?.rowspan || 1;
    if (cs > 1) attrs.push(` colspan="${cs}"`);
    if (rs > 1) attrs.push(` rowspan="${rs}"`);
    return `<${tag}${attrs.join('')}>${escapeHtml(cellText(cell, ' '))}</${tag}>`;
  };

  const rowHtml = (row) => {
    const cells = Array.isArray(row?.content) ? row.content : [];
    const inner = cells
      .map((c) => cellHtml(c, c.type === 'tableHeader' ? 'th' : 'td'))
      .join('');
    return `<tr>${inner}</tr>`;
  };

  let html;
  if (firstRowIsHeader) {
    const thead = `<thead>${rowHtml(rows[0])}</thead>`;
    const tbody = `<tbody>${rows.slice(1).map(rowHtml).join('')}</tbody>`;
    html = `<table>${thead}${tbody}</table>`;
  } else {
    const tbody = `<tbody>${rows.map(rowHtml).join('')}</tbody>`;
    html = `<table>${tbody}</table>`;
  }

  return { gfm, html, hasMerges };
};

/**
 * Normalize a prosemirror-tables `CellSelection.content()` Slice into a
 * standalone table JSON. The slice content is either the whole `table` node
 * (when the selection covers the entire table) or a bare list of `tableRow`
 * nodes (a partial rectangle) — we wrap the latter in a synthetic table.
 *
 * @param {import('@tiptap/pm/model').Fragment} fragment
 * @returns {object|null} table JSON or null
 */
const tableJsonFromSelectionFragment = (fragment) => {
  if (!fragment || fragment.childCount === 0) return null;

  const first = fragment.firstChild;
  if (first?.type?.name === 'table') {
    return first.toJSON();
  }

  const rows = [];
  fragment.forEach((rowNode) => {
    if (rowNode?.type?.name === 'tableRow') rows.push(rowNode.toJSON());
  });
  if (!rows.length) return null;

  return { type: 'table', content: rows };
};

/**
 * Serialize the current rectangular cell selection (a prosemirror-tables
 * `CellSelection`) into `{ gfm, html, hasMerges }`, containing ONLY the
 * selected rectangle of rows × columns. Header cells are preserved when the
 * selection covers them (they stay `tableHeader` in the extracted rows).
 *
 * Returns `null` when the given editor state does not currently hold a
 * `CellSelection` (e.g. a plain text cursor) — callers should fall back to
 * serializing the whole table in that case.
 *
 * @param {import('@tiptap/pm/state').EditorState} state
 * @returns {{ gfm: string, html: string, hasMerges: boolean }|null}
 */
export const serializeCellSelection = (state) => {
  const selection = state?.selection;
  // Duck-type a prosemirror-tables CellSelection (it exposes $anchorCell /
  // $headCell, which no other selection type has). Reason: importing
  // `CellSelection` from `@tiptap/pm/tables` here would drag ESM/TS-only
  // package sources into this otherwise dependency-free, Jest-friendly module.
  const isCellSelection = Boolean(
    selection && selection.$anchorCell && selection.$headCell
      && typeof selection.content === 'function'
  );
  if (!isCellSelection) return null;

  const slice = selection.content();
  const tableJson = tableJsonFromSelectionFragment(slice?.content);
  if (!tableJson) return null;

  return serializeTable(tableJson);
};

export default serializeTable;
